import { CSSProperties, ReactElement } from 'react';
import { staticFile } from 'remotion';

// Components
import { FallbackRemotionImg } from '@/components/common/FallbackRemotionImg';

// Utils
import { combineClasses } from '@/utils/className';
import { processImageUrlForRemotion } from '@/utils/url';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Fonts
import { lalezarFontFamily } from '@/styles/fonts';

// Constants
import {
  NOTCH_OFFSET,
  NOTCH_RADIUS,
  PROJECT_LABEL_OVERFLOW,
  DOT_SIZE,
} from '../../constants';

interface TeamMemberCardProps {
  firstName: string;
  lastName: string;
  width: number;
  height: number;
  badgeText?: string;
  imageUrl?: string;
  flipProgress?: number;
  style?: CSSProperties;
  className?: string;
}

export const TeamMemberCard = ({
  firstName,
  lastName,
  badgeText,
  imageUrl,
  width,
  height,
  flipProgress = 1,
  style,
  className,
}: TeamMemberCardProps): ReactElement => {
  const processedImgSrc = processImageUrlForRemotion(imageUrl, staticFile);
  const { colors, typography } = employeeYearInReviewTheme;

  // Create clip-path that cuts out a circle at top-right area (front face)
  const notchCenterX = width - NOTCH_OFFSET;
  const clipPath = `path('M 0 0 L ${notchCenterX - NOTCH_RADIUS} 0 A ${NOTCH_RADIUS} ${NOTCH_RADIUS} 0 0 0 ${notchCenterX + NOTCH_RADIUS} 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z')`;

  // Create clip-path with notch on the left side (back face)
  // Include extra height to cover the project label area
  const backNotchCenterX = NOTCH_OFFSET;
  const backFullHeight = height + PROJECT_LABEL_OVERFLOW;
  const backClipPath = `path('M 0 0 L ${backNotchCenterX - NOTCH_RADIUS} 0 A ${NOTCH_RADIUS} ${NOTCH_RADIUS} 0 0 0 ${backNotchCenterX + NOTCH_RADIUS} 0 L ${width} 0 L ${width} ${backFullHeight} L 0 ${backFullHeight} Z')`;

  // Flip rotation: 0 progress = 180deg (back facing), 1 progress = 0deg (front facing)
  const rotateY = 180 - flipProgress * 180;

  return (
    <div
      className={combineClasses('relative', className)}
      style={{
        width,
        height,
        perspective: 1200,
        ...style,
      }}
    >
      {/* 3D flip container */}
      <div
        className="relative size-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateY(${rotateY}deg)`,
        }}
      >
        {/* Front face - Member info */}
        <div
          className="absolute inset-0 size-full"
          style={{
            backfaceVisibility: 'hidden',
          }}
        >
          {/* Card with clip-path */}
          <div
            className={combineClasses(
              colors.bgMemberCard,
              'shadow-2xl size-full flex flex-col p-[15px]',
            )}
            style={{
              clipPath,
            }}
          >
            {/* Name section */}
            <div
              className={combineClasses(
                typography.md,
                colors.textPrimary,
                'uppercase leading-[42px] pt-6',
              )}
              style={{
                fontFamily: lalezarFontFamily,
              }}
            >
              <div>{firstName}</div>
              <div>{lastName}</div>
            </div>

            {/* Photo container - takes remaining space */}
            <div
              className={combineClasses(
                'w-full overflow-hidden mt-3',
                colors.bgSecondary,
              )}
            >
              {processedImgSrc && (
                <FallbackRemotionImg
                  src={processedImgSrc}
                  className="size-[350px] object-cover object-top"
                />
              )}
            </div>
          </div>

          {/* Project label - at bottom */}
          {badgeText && (
            <div
              className={combineClasses(
                'absolute left-0 right-0 w-full mt-2 -bottom-13',
                colors.badgeBackground.quaternary,
              )}
            >
              <div className="relative h-16">
                <div
                  className={combineClasses(
                    colors.badgeBackground.tertiary,
                    'mx-auto text-center absolute top-1/2 -translate-y-1/2 translate-x-8 w-full py-1',
                  )}
                >
                  <p
                    className={combineClasses(
                      colors.badgeText,
                      typography['3xs'],
                      'text-white font-medium line-clamp-1 py-1',
                    )}
                  >
                    {badgeText}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Decorative dot at notch position */}
          <div
            className={combineClasses(
              'absolute rounded-full size-9 -top-4.5',
              colors.bgMemberCard,
            )}
            style={{
              right: NOTCH_OFFSET - DOT_SIZE / 2,
            }}
          />
        </div>

        {/* Back face - Logo */}
        <div
          className="absolute inset-0 size-full"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div
            className={combineClasses(
              'flex items-center justify-center shadow-2xl w-full',
              colors.bgMemberCard,
            )}
            style={{
              height: backFullHeight,
              clipPath: backClipPath,
            }}
          />

          {/* Decorative dot at back notch position (left side) */}
          <div
            className={combineClasses(
              'absolute rounded-full size-9 -top-4.5',
              colors.bgMemberCard,
            )}
            style={{
              left: NOTCH_OFFSET - DOT_SIZE / 2,
            }}
          />
        </div>
      </div>
    </div>
  );
};
